import React, { useRef, useState } from "react";
import OrgNummer from "personnummer.js";
import { useTranslation } from "react-i18next";
import { geocodeByAddress, getLatLng } from "react-places-autocomplete";
import { useHistory } from "react-router";
import { InputField } from "components/designV2/inputfield/inputfield";
import { ProgressIndicator } from "components/designV2/progressIndicator/progressIndicator";
import { Text } from "components/designV2/text/text";
import { ColorV2 } from "config/colors";
import { useAuthStore } from "stores/authStore/authStore";
import { Api } from "services/api/api.service";
import type { LocationSelection } from "web-apps/company/components/location/locationMap.logic";
import { LocationMap } from "web-apps/company/components/location/locationMap.component";
import { COMPANY_AUTH_PRIMARY_BUTTON_STYLES } from "web-apps/company/components/shared/companyAuth.buttonStyles.contract";
import { useCompanyStore } from "web-apps/company/stores/companyStore/companyStore";
import {
  formatCityName,
  hasCompleteAddress,
  parseCompanyInformationResponse,
} from "./companyInfoForm.logic";
import {
  ActionsRow,
  BackButton,
  FieldLabel,
  ContinueButton,
  FieldsWrapper,
  StepLabel,
  StyledForm,
  StyledTitle,
} from "./companyInfoForm.styled";

type Props = {
  city: string;
  companyAdress: string;
  companyName: string;
  legalName: string;
  location: string;
  page: number;
  postalCode: string;
  registrationNumber: string;
  setCity: React.Dispatch<React.SetStateAction<string>>;
  setCompanyAdress: React.Dispatch<React.SetStateAction<string>>;
  setCompanyName: React.Dispatch<React.SetStateAction<string>>;
  setLegalName: React.Dispatch<React.SetStateAction<string>>;
  setLocation: React.Dispatch<React.SetStateAction<string>>;
  setPage: React.Dispatch<React.SetStateAction<number>>;
  setPostalCode: React.Dispatch<React.SetStateAction<string>>;
  setRegistrationNumber: React.Dispatch<React.SetStateAction<string>>;
};

export const CompanyInfoForm: React.FC<Props> = ({
  city,
  companyAdress,
  companyName,
  legalName,
  location,
  page,
  postalCode,
  registrationNumber,
  setCity,
  setCompanyAdress,
  setCompanyName,
  setLegalName,
  setLocation,
  setPage,
  setPostalCode,
  setRegistrationNumber,
}) => {
  const { t } = useTranslation();
  const requiredLabel = t("System.Required").toLowerCase();
  const history = useHistory();
  const [, authActions] = useAuthStore();
  const [registrationNumberError, setRegistrationNumberError] = useState(false);
  const [isCompanyInformationLoading, setIsCompanyInformationLoading] =
    useState(false);
  const [isAdvancing, setIsAdvancing] = useState(false);
  const [companyState] = useCompanyStore();
  const companyInformationRequestIdRef = useRef(0);

  const hasValue = (value: string): boolean => value.trim().length > 0;

  const validateAddress = (): boolean =>
    Boolean(
      hasValue(city) &&
      hasValue(companyAdress) &&
      hasValue(postalCode) &&
      hasValue(location),
    );

  const validate = (): boolean =>
    Boolean(
      hasValue(companyName) &&
      hasValue(registrationNumber) &&
      hasValue(legalName) &&
      hasValue(city) &&
      validateAddress() &&
      !registrationNumberError,
    );

  const clearCompanyInformationFields = (): void => {
    setLegalName("");
    setCompanyAdress("");
    setPostalCode("");
    setCity("");
    setLocation("");
  };

  const handleLocationChange = (selection: LocationSelection | null): void => {
    setCompanyAdress(selection?.streetAddress ?? "");
    setCity(selection?.city ?? "");
    setPostalCode(selection?.postalCode ?? "");
    setLocation(selection?.location ?? "");
  };

  const validateRegistrationNumber = (value: string): boolean => {
    const requestId = companyInformationRequestIdRef.current + 1;
    companyInformationRequestIdRef.current = requestId;
    clearCompanyInformationFields();

    if (OrgNummer.validateCIN(value)) {
      setRegistrationNumberError(false);
      setIsCompanyInformationLoading(true);
      void fetchCompanyInfo(value, requestId);
      return true;
    } else {
      setRegistrationNumberError(true);
      setIsCompanyInformationLoading(false);
      return false;
    }
  };

  const fetchCompanyInfo = async (
    number: string,
    requestId: number,
  ): Promise<void> => {
    const isCurrentRequest = (): boolean =>
      companyInformationRequestIdRef.current === requestId;
    const setLookupError = (): void => {
      if (isCurrentRequest()) {
        setIsCompanyInformationLoading(false);
      }
    };

    try {
      const response = await Api().company.getCompanyInformation(number);

      if (!isCurrentRequest()) {
        return;
      }

      const companyInformation = parseCompanyInformationResponse(response.data);

      if (!companyInformation) {
        setLookupError();
        return;
      }

      if (companyInformation.namn) {
        setLegalName(companyInformation.namn);
      }

      if (!hasCompleteAddress(companyInformation.adress)) {
        setIsCompanyInformationLoading(false);
        return;
      }

      const companyAddress = companyInformation.adress;
      const formattedCity = formatCityName(companyAddress.postort);

      setCompanyAdress(companyAddress.postadress);
      setPostalCode(companyAddress.postnummer);
      setCity(formattedCity);
      setIsCompanyInformationLoading(false);

      try {
        const geoData = await geocodeByAddress(
          `${companyAddress.postadress}, ${companyAddress.postort}`,
        );

        if (!isCurrentRequest()) {
          return;
        }

        const firstGeoResult = geoData[0];

        if (!firstGeoResult) {
          return;
        }

        const geoLocation = await getLatLng(firstGeoResult);

        if (!isCurrentRequest()) {
          return;
        }

        if (
          Number.isFinite(geoLocation.lat) &&
          Number.isFinite(geoLocation.lng)
        ) {
          setLocation(`${geoLocation.lat}, ${geoLocation.lng}`);
        }
      } catch {
        if (isCurrentRequest()) {
          setIsCompanyInformationLoading(false);
        }
      }
    } catch {
      setLookupError();
    }
  };

  const cancelRegistration = async (): Promise<void> => {
    if (companyState.company) {
      history.push("/company/schedule");
      return;
    }

    try {
      await authActions.signOut();
    } catch {
      // Cancellation should still return the user to login if sign-out cleanup fails.
    }

    history.push("./login");
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (isAdvancing) {
      return;
    }

    if (validate()) {
      setIsAdvancing(true);
      setPage(page + 1);
    }
  };

  return (
    <StyledForm onSubmit={handleSubmit}>
      <StepLabel>
        {t("RegisterCompany.StepOfTotal", { step: 1, total: 3 })}
      </StepLabel>
      <ProgressIndicator steps={3} currentStep={1} />
      <StyledTitle>{t("RegisterCompany.CompanyInformation")}</StyledTitle>
      <FieldsWrapper>
        <InputField
          label={`${t("RegisterCompany.RegistrationNumber")} (${requiredLabel})`}
          type="text"
          value={registrationNumber}
          placeholder="XXXXXX-XXXX"
          fullWidth
          hasError={registrationNumberError}
          errorText={t("RegisterCompany.InvalidRegistrationNumber")}
          onChange={(value) => {
            const trimmedValue = value.trim();
            validateRegistrationNumber(trimmedValue);
            setRegistrationNumber(trimmedValue);
          }}
          onClear={() => {
            companyInformationRequestIdRef.current += 1;
            setRegistrationNumber("");
            setRegistrationNumberError(false);
            setIsCompanyInformationLoading(false);
            clearCompanyInformationFields();
          }}
        />
        <InputField
          label={`${t("RegisterCompany.LegalName")} (${requiredLabel})`}
          type="text"
          value={legalName}
          placeholder={t("RegisterCompany.LegalName")}
          fullWidth
          onChange={setLegalName}
          onClear={() => setLegalName("")}
        />
        <FieldLabel>
          {`${t("RegisterCompany.CompanyAddress")} (${requiredLabel})`}
        </FieldLabel>
        <LocationMap
          key={registrationNumber}
          address={companyAdress && city ? `${companyAdress}, ${city}` : ""}
          onLocationChange={handleLocationChange}
          inRegistration={true}
          noMap
        />
        {!validateAddress() && companyAdress !== "" && (
          <Text color={ColorV2.NegativeRed} typography="s1">
            {t("RegisterCompany.LocationHelperText")}
          </Text>
        )}
        <InputField
          label={`${t("RegisterCompany.DisplayName")} (${requiredLabel})`}
          type="text"
          value={companyName}
          placeholder={t("RegisterCompany.DisplayName")}
          fullWidth
          onChange={setCompanyName}
          onClear={() => setCompanyName("")}
        />
      </FieldsWrapper>

      <ActionsRow>
        <BackButton
          type="button"
          label={t("General.Back")}
          onClick={cancelRegistration}
        />
        <ContinueButton
          type="submit"
          label={t("RegisterCompany.Continue")}
          disabled={
            !validate() ||
            page !== 0 ||
            isAdvancing ||
            isCompanyInformationLoading
          }
          styleOverrides={COMPANY_AUTH_PRIMARY_BUTTON_STYLES}
        />
      </ActionsRow>
    </StyledForm>
  );
};
